import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Row, Col, Button } from 'react-bootstrap';
import { dashboardApi, conversionsApi } from '../../services/api';
import './Dashboard.css';
import UpgradePrompt from '../../components/UpgradePrompt';
import SubscriptionCard from '../../components/dashboard/SubscriptionCard';
import UsageLimitBanner from '../../components/dashboard/UsageLimitBanner';


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalLinks: 0,
    totalClicks: 0,
    totalConversions: 0,
    conversionRate: 0,
    revenue: 0,
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('week'); // week, month, year
  const [metric, setMetric] = useState('clicks'); // clicks, conversions
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeType, setUpgradeType] = useState('click');
  const [subscription, setSubscription] = useState(null);

  // Convert dateRange to appropriate period format for API
  const getPeriodFormatForAPI = (range) => {
    switch (range) {
      case 'week': return '7d';
      case 'month': return '30d';
      case 'year': return '1y';
      default: return '30d';
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        const period = getPeriodFormatForAPI(dateRange);

        // Get dashboard stats
        try {
          const statsResponse = await dashboardApi.getStats(period);
          if (statsResponse.data.status === 'success') {
            const { 
              counts = {}, 
              conversionRate = 0, 
              subscription: subscriptionData = null
            } = statsResponse.data.data || {};
            
            // Extract data with defaults to prevent undefined errors
            const totalLinks = counts?.links || 0;
            const totalClicks = counts?.clicks?.total || 0;
            const totalConversions = counts?.conversions?.total || 0;
            const totalValue = counts?.conversions?.totalValue || 0;
            
            setStats({
              totalLinks,
              totalClicks,
              totalConversions,
              conversionRate: parseFloat(conversionRate) || 0,
              revenue: parseFloat(totalValue) || 0
            });
            
            setSubscription(subscriptionData);
          }
        } catch (statsError) {
          console.error('Error fetching dashboard stats:', statsError);
          // Continue trying to load chart data even if stats fail
        }

        // Get chart data
        try {
          let chartResponse;
          if (metric === 'clicks') {
            chartResponse = await dashboardApi.getChartData(period, 'clicks');
          } else {
            chartResponse = await conversionsApi.getConversionChartData(periodToDays(dateRange));
          }

          if (chartResponse.data.status === 'success') {
            // Access chart data from standardized response structure
            const data = chartResponse.data.data?.chartData || [];
            
            const labels = data.map(item => item.date);
            const values = data.map(item => item.count);
            
            setChartData({
              labels,
              datasets: [
                {
                  label: metric === 'clicks' ? 'Clicks' : 'Conversions',
                  data: values,
                  borderColor: metric === 'clicks' ? 'rgb(52, 152, 219)' : 'rgb(46, 204, 113)',
                  backgroundColor: metric === 'clicks' ? 'rgba(52, 152, 219, 0.5)' : 'rgba(46, 204, 113, 0.5)',
                  tension: 0.1,
                }
              ],
            });
          }
        } catch (chartError) {
          console.error('Error fetching chart data:', chartError);
          // Set default empty chart data
          setChartData({
            labels: [],
            datasets: [{
              label: metric === 'clicks' ? 'Clicks' : 'Conversions',
              data: [],
              borderColor: metric === 'clicks' ? 'rgb(52, 152, 219)' : 'rgb(46, 204, 113)',
              backgroundColor: metric === 'clicks' ? 'rgba(52, 152, 219, 0.5)' : 'rgba(46, 204, 113, 0.5)',
            }]
          });
        }

        setLoading(false);
      } catch (err) {
        setLoading(false);
        
        // Show upgrade prompt if backend blocks due to limits
        const backendMsg = err.response?.data?.message || '';
        if (backendMsg.toLowerCase().includes('click limit')) {
          setUpgradeType('click');
          setShowUpgradePrompt(true);
        } else if (backendMsg.toLowerCase().includes('conversion limit')) {
          setUpgradeType('conversion');
          setShowUpgradePrompt(true);
        } else {
          console.error('Error fetching dashboard data:', err);
          setError(
            err.response?.status === 500 
              ? 'Server error. We\'re working on fixing this issue.'
              : 'Failed to load dashboard data. Please try again later.'
          );
        }
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateRange, metric]);

  const periodToDays = (period) => {
    switch (period) {
      case 'week': return 7;
      case 'month': return 30;
      case 'year': return 365;
      default: return 30;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <h1>Dashboard</h1>
        <UsageLimitBanner />
        <p>{error}</p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }
  return (
    <div className="dashboard-page">
      {showUpgradePrompt && (
        <UpgradePrompt type={upgradeType} onClose={() => setShowUpgradePrompt(false)} />
      )}
      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="ml-2" 
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </div>
      )}
      <UsageLimitBanner />
      <div className="page-header">
        <h1>Dashboard</h1>
        <div className="date-range-selector">
          <button
            className={`btn-sm ${dateRange === 'week' ? 'active' : ''}`}
            onClick={() => setDateRange('week')}
          >
            Week
          </button>
          <button
            className={`btn-sm ${dateRange === 'month' ? 'active' : ''}`}
            onClick={() => setDateRange('month')}
          >
            Month
          </button>
          <button
            className={`btn-sm ${dateRange === 'year' ? 'active' : ''}`}
            onClick={() => setDateRange('year')}
          >
            Year
          </button>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">🔗</div>
          <div className="stat-value">{stats.totalLinks}</div>
          <div className="stat-label">Total Links</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👆</div>
          <div className="stat-value">{stats.totalClicks}</div>
          <div className="stat-label">Total Clicks</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-value">{stats.totalConversions}</div>
          <div className="stat-label">Conversions</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-value">{(stats.conversionRate || 0).toFixed(2)}%</div>
          <div className="stat-label">Conversion Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💵</div>
          <div className="stat-value">${(stats.revenue || 0).toFixed(2)}</div>
          <div className="stat-label">Revenue</div>
        </div>
      </div>

      <div className="dashboard-chart card">
        <h2>Performance Overview</h2>
        <div className="chart-controls">
          <button
            className={`btn-sm ${metric === 'clicks' ? 'active' : ''}`}
            onClick={() => setMetric('clicks')}
          >
            Clicks
          </button>
          <button
            className={`btn-sm ${metric === 'conversions' ? 'active' : ''}`}
            onClick={() => setMetric('conversions')}
          >
            Conversions
          </button>
        </div>
        <Line 
          data={chartData} 
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                min: 0,
                suggestedMin: 0,
                ticks: {
                  callback: function(value) {
                    // Ensure value is a number before calling toFixed
                    return (value || 0).toFixed(1);
                  }
                }
              }
            }
          }} 
        />
      </div>

      <Row className="mt-4">
        <Col lg="4">
          <SubscriptionCard subscription={subscription} />
        </Col>
        <Col lg="8" className="dashboard-insights">
          <div className="card mb-4">
            <h2>Pro Insights</h2>
            <div className="insights-content">
              {subscription?.status === 'active' ? (
                <>
                  <p>As a Pro user, you have access to:</p>
                  <ul>
                    <li>Unlimited affiliate links</li>
                    <li>Unlimited clicks/month</li>
                    <li>Unlimited conversions/month</li>
                    <li>Advanced dashboard</li>
                    <li>Export data capabilities</li>
                    <li>Priority support</li>
                  </ul>
                  <Button as={Link} to="/dashboard/invoices" variant="outline-primary" size="sm">
                    View Billing History
                  </Button>
                </>
              ) : (
                <>
                  <p>Upgrade to Pro to unlock:</p>
                  <ul>
                    <li>Unlimited affiliate links</li>
                    <li>Unlimited clicks/month</li>
                    <li>Unlimited conversions/month</li>
                    <li>Advanced dashboard</li>
                    <li>Export data capabilities</li>
                    <li>Priority support</li>
                  </ul>
                  <Button as={Link} to="/dashboard/subscription" variant="primary">
                    Upgrade to Pro
                  </Button>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>


      <div className="dashboard-actions mt-4 mb-3 d-flex gap-3">
        <Link to="/dashboard/links/create" className="btn btn-primary">
          Create New Link
        </Link>
        <Link to="/dashboard/links" className="btn btn-outline-secondary">
          View All Links
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
